/**
 * ItemsGrid.tsx — Renders a grid/flex layout of child `SectionData` panels.
 *
 * JSON trigger: `uiCompType.method: "renderItemsGrid"`
 * Data field:   `sectionData.gridItems` (array of `SectionData`)
 *
 * **Grid modes** (set via `uiCompType.options.grid`):
 *
 * | Mode      | Layout engine | Description |
 * |---        |---            |---          |
 * | `spread`  | MUI Grid      | Items expand to fill available columns equally |
 * | `center`  | MUI Grid      | Items centred; columns controlled by `xs`/`md`/`lg` per item |
 * | (default) | CSS Flexbox   | `display:flex; flex-wrap:wrap` on the container |
 *
 * **Per-item MUI Grid options** (on each `gridItem.uiCompType.options`):
 *  - `xs`, `md`, `lg` — MUI Grid column spans (1–12 or `'auto'`)
 *  - `width`, `height` — Explicit dimensions forwarded to the item wrapper
 *
 * **Mobile overrides:** `uiCompType.mStyles` and `uiCompType.mItemStyles`
 * override `styles` and `itemStyles` when `deviceType === 'Mobile'`.
 *
 * Each child is dispatched via `renderSection`, so any component can be a grid
 * item (Panel, CustImage, CustVideo, etc.).
 *
 * **Style slots:** `styles`/`mStyles` (container), `itemStyles`/`mItemStyles` (each item).
 */
import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps, renderSection } from "./index";
import { useDeviceType } from "@/hooks/useDeviceType";

interface ItemsGridProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const ItemsGrid = ({ sectionData, props }: ItemsGridProps): JSX.Element | null => {
    const deviceType = useDeviceType();
    const baseStyles = { ...sectionData.uiCompType?.styles };
    const mobileStyles = { ...sectionData.uiCompType?.mStyles };
    const baseItemStyles = { ...sectionData.uiCompType?.itemStyles };
    const mobileItemStyles = { ...sectionData.uiCompType?.mItemStyles };

    const [styles, setStyles] = useState(baseStyles);
    const [parentStyles, setParentStyles] = useState(baseItemStyles);
    
    useEffect(() => {
        if (deviceType === "Mobile") {
            if (mobileStyles) {
                setStyles({ ...baseStyles, ...mobileStyles });
            }
            if (mobileItemStyles) {
                setParentStyles({ ...baseItemStyles, ...mobileItemStyles });
            }
        } else {
            setStyles(baseStyles);
            setParentStyles(baseItemStyles);
        }
    }, [deviceType]);

    const items = sectionData.items;
    let swapItemsClass = sectionData.uiCompType?.options?.swapItems ? cssStyles.swapItems
        : (sectionData.uiCompType?.options?.swapColumnItems ? cssStyles.swapColumnItems
            : (sectionData.uiCompType?.options?.swapRowItems ? cssStyles.swapRowItems : ''));
    const isCenterGrid = sectionData.uiCompType?.options?.grid === 'center';
    //console.log("ItemsGrid - items:", items, " styles:", styles, " parentStyles:", parentStyles, " swapItemsClass:", swapItemsClass, " isCenterGrid:", isCenterGrid);

    return (
        <>
            {sectionData.uiCompType?.options?.grid === 'flex' ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', ...styles }} className={`${cssStyles.gridContainer} ${swapItemsClass}`}>
                    {items?.map((item: any, index: number) => {
                        const itemParentStyles = deviceType === "Mobile" 
                            ? { ...item.uiCompType?.parentStyles, ...item.uiCompType?.mParentStyles } 
                            : item.uiCompType?.parentStyles;
                        const itemStyle = { ...parentStyles, ...itemParentStyles };
                        return (
                            <div
                                key={index}
                                style={{
                                    flex: `1 0 ${item.uiCompType?.options?.width ?? `${100 / items.length}%`}`,
                                    boxSizing: 'border-box',
                                    padding: item.uiCompType?.options?.spacing || '8px',
                                    ...itemStyle
                                }}
                            >
                                {renderSection(item.trackId, item, props)}
                            </div>
                        )
                    })}
                </div>
            ) : (
                <Grid container
                    justifyContent={isCenterGrid ? 'center' : undefined}
                    spacing={sectionData.uiCompType?.options?.spacing ?? 2}
                    className={`${cssStyles.gridContainer} ${swapItemsClass}`} style={styles} sx={styles}>
                    {items?.map((itemObj: any, index: number) => {
                        const itemParentStyles = deviceType === "Mobile" 
                            ? { ...itemObj.uiCompType?.parentStyles, ...itemObj.uiCompType?.mParentStyles } : itemObj.uiCompType?.parentStyles;
                        const itemStyle = { ...parentStyles, ...itemParentStyles };
                        return (
                            <Grid item className={cssStyles.gridItem}
                                style={itemStyle}
                                xs={itemObj?.uiCompType?.options?.xs ?? (isCenterGrid ? 'auto' : 12)}
                                md={itemObj?.uiCompType?.options?.md ?? (isCenterGrid ? 'auto' : (12 / items.length))}
                                lg={itemObj?.uiCompType?.options?.lg ?? (isCenterGrid ? 'auto' : (12 / items.length))}
                                key={index}>
                                {renderSection(itemObj.trackId, itemObj, props)}
                            </Grid>
                        )
                    })}
                </Grid>
            )}
        </>
    );
}
export default ItemsGrid;
